/* eslint no-underscore-dangle: ["error", { "allow": [ "_id"] }] */
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, GridToolbarQuickFilter } from '@mui/x-data-grid';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSkeleton from '../../utils/LoadingSkeleton';
import NoRowsOverlay from '../../utils/NoRowOverlay';
import ClientAdd from './add';
import ClientImport from './import';
import ClientEdit from './edit';
import ClientView from './view';
import ClientDelete from './delete';
import ClientAction from '../../store/actions/ClientAction';
import DeptAction from '../../store/actions/DepartmentAction';
import CopyInput from '../../components/CopyInput';
import useHasPermission from '../../utils/useHasPermission';
import { client, show, store, update, destroy } from '../../utils/constants';

function QuickSearchToolbar() {
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  return (
    <Box
      className="tableTopWrap page__title"
      sx={{
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3
      }}
    >
      <Stack direction="row" sx={{ ml: 'auto' }} spacing={2} className="MuiDataGrid-Search">
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) => searchInput.split(',').map((value) => value.trim())}
          quickFilterFormatter={(quickFilterValues) => quickFilterValues.join(', ')}
          debounceMs={800} // time before applying the new quick filter value
          variant="outlined"
          size="small"
          sx={{ pb: 0, maxWidth: '275px' }}
        />
        {useHasPermission(client, store) && (
          <Button
            className="button_primary"
            variant="outlined"
            size="small"
            color="secondary"
            onClick={() => {
              setImportOpen(true);
            }}
          >
            Import Clients
          </Button>
        )}
        {useHasPermission(client, store) && (
          <Button
            className="button_primary"
            variant="contained"
            size="small"
            color="secondary"
            onClick={() => {
              setAddOpen(true);
            }}
          >
            Add Client
          </Button>
        )}
      </Stack>
      {addOpen === true ? (
        <ClientAdd openmodal={addOpen} onCloseModal={() => setAddOpen(false)} />
      ) : (
        ''
      )}
      {importOpen === true ? (
        <ClientImport openmodal={importOpen} onCloseModal={() => setImportOpen(false)} />
      ) : (
        ''
      )}
    </Box>
  );
}
// QuickSearchToolbar.propTypes = {
//   activeDepartment: PropTypes.arrayOf.isRequired
// };

export default function ClientList() {
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10
  });

  const [sortInfo, setSortInfo] = React.useState({
    sortField: '',
    sort: ''
  });
  const [editOpen, setEditOpen] = React.useState(false);
  const [viewOpen, setViewOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [itemID, setItemID] = React.useState('');
  const [rowCountState, setRowCountState] = React.useState(0);
  const [data, setData] = React.useState([]);
  const [departmentName, setDepartmentName] = React.useState('');
  const [searchKey, setSearchKey] = React.useState('');
  const dispatch = useDispatch();
  const { clientListData, isLoading } = useSelector((state) => state.client); // Replace 'data' with your reducer name
  const { activeDeptData } = useSelector((state) => state.department);
  const { paginationData, sortingData, keyword, department } = useSelector(
    (state) => state.client.queryParams
  );

  useEffect(() => {
    dispatch(ClientAction.updateQueryParams(paginationModel, sortInfo, searchKey, departmentName));
    dispatch(ClientAction.getAll(paginationModel, sortInfo, searchKey, departmentName));
  }, [dispatch, paginationModel, sortInfo, departmentName, searchKey, departmentName]);

  useEffect(() => {
    dispatch(DeptAction.getActiveItem());
  }, [dispatch]);

  useEffect(() => {
    if (clientListData) {
      if (clientListData.data) {
        setData(clientListData.data);
      } else {
        setData([]);
      }
      setRowCountState((prevRowCountState) =>
        clientListData.total !== undefined ? clientListData.total : prevRowCountState
      );
    }
  }, [clientListData]);

  useEffect(() => {
    if (paginationData) {
      setPaginationModel(paginationData);
    }
    if (sortingData) {
      setSortInfo(sortingData);
    }
    if (keyword) {
      setSearchKey(keyword);
    }
    if (department) {
      setDepartmentName(department);
    }
  }, [paginationData, keyword, sortingData, department]);

  const columns = [
    {
      field: 'unique_id',
      headerName: '',
      sortable: false,
      align: 'left',
      renderCell: (params) => {
        if (params && params.value) {
          return <CopyInput id={params.value} />;
        }
        return null;
      }
    },
    {
      field: 'company_name',
      headerName: 'Company Name',
      flex: 1
      // sortable: false,
    },
    {
      field: 'name',
      headerName: 'Client Name',
      flex: 1
    },
    {
      field: 'email',
      headerName: 'Email ID',
      flex: 1
    },
    {
      field: 'department_name',
      sortable: false,
      headerName: 'Department Name',
      flex: 1
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      renderCell: (params) => {
        if (params && params.value) {
          return params.value === 1 ? (
            <Chip label="Active" color="success" />
          ) : (
            <Chip label="Inactive" color="lignhtwarning" />
          );
        }
        return null;
      }
    },
    useHasPermission(client) && {
      field: 'actions',
      type: 'actions',
      flex: 0.5,
      headerName: 'Actions',
      getActions: (params) => [
        client && useHasPermission(client, show) ? (
          <GridActionsCellItem
            key={0}
            icon={<i className="icon-view" />}
            className="actionBtn"
            label="View"
            title="View"
            onClick={() => {
              setViewOpen(true);
              setItemID(params.id);
            }}
          />
        ) : (
          <span />
        ),
        client && useHasPermission(client, update) ? (
          <GridActionsCellItem
            key={1}
            icon={<i className="icon-edit" />}
            className="actionBtn"
            title="Edit"
            label="Edit"
            onClick={() => {
              setEditOpen(true);
              setItemID(params.id);
            }}
          />
        ) : (
          <span />
        ),
        client && useHasPermission(client, destroy) ? (
          <GridActionsCellItem
            key={2}
            icon={<i className="icon-delete" />}
            className="actionBtn"
            label="Delete"
            title="Delete"
            onClick={() => {
              setDeleteOpen(true);
              setItemID(params.id);
            }}
          />
        ) : (
          <span />
        )
      ]
    }
  ];

  const handleSortModelChange = React.useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      const sortData = {
        sortField: field,
        sort
      };
      setSortInfo(sortData);
    }
    // eslint-disable-next-line
  }, []);

  const onFilterChange = React.useCallback((filterModel) => {
    // Here you save the data you need from the filter model
    const keywordData = filterModel.quickFilterValues[0];
    setSearchKey(keywordData);
    setPaginationModel({ page: 0, pageSize: 10 });
    // dispatch(ClientAction.getAll(paginationModel, sortInfo, keyword));
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ mb: 3 }} className="cardWrapper">
      <div className="pageTitleWrap">
        <Typography className="" component="h2" variant="h2">
          Clients
        </Typography>
      </div>
      <Card className="" sx={{ mb: 3 }}>
        <CardContent>
          <Autocomplete
            className="Department"
            size="small"
            options={activeDeptData ?? []}
            onChange={(e, newvalue) => {
              if (newvalue && newvalue?.name) {
                setDepartmentName(newvalue.name);
              } else {
                setDepartmentName('');
              }
              setPaginationModel({ page: 0, pageSize: 10 });
            }}
            sx={{ width: '400px', m: '0 !important' }}
            getOptionLabel={(option) => option.name}
            renderOption={(props, option) => {
              return (
                <li {...props} key={option._id}>
                  {option.name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Department"
                size="small"
                placeholder="Search Department"
                variant="outlined"
                margin="none"
              />
            )}
          />
        </CardContent>
      </Card>
      <Box sx={{ bgcolor: 'white' }} className="dataTable__wrap">
        <DataGrid
          rows={data}
          getRowId={(row) => row._id}
          rowCount={rowCountState}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              }
            }
          }}
          autoHeight
          sx={{ minHeight: 650, '--DataGrid-overlayHeight': '50vh' }}
          disableColumnFilter
          rowHeight={50}
          slots={{
            toolbar: QuickSearchToolbar,
            loadingOverlay: LoadingSkeleton,
            noRowsOverlay: NoRowsOverlay
          }}
          // slotProps={{
          //   toolbar: { activeDepartment: activeDeptData }
          // }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          filterMode="server"
          onFilterModelChange={onFilterChange}
          disableRowSelectionOnClick
          loading={isLoading}
        />
      </Box>

      {editOpen === true ? (
        <ClientEdit openmodal={editOpen} itemId={itemID} onCloseModal={() => setEditOpen(false)} />
      ) : (
        ''
      )}
      {viewOpen === true ? (
        <ClientView openmodal={viewOpen} itemId={itemID} onCloseModal={() => setViewOpen(false)} />
      ) : (
        ''
      )}
      {deleteOpen === true ? (
        <ClientDelete
          openmodal={deleteOpen}
          itemId={itemID}
          onCloseModal={() => setDeleteOpen(false)}
        />
      ) : (
        ''
      )}
    </Box>
  );
}
